import { SCORM } from 'pipwerks-scorm-api-wrapper';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import getStore from 'services/getStore';
import { GetParams } from 'setup';
import uuid from 'uuidv4';
import { ModuleEvent } from './events';

const readCookie = (name: string) => {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

type HostingContext = ReturnType<typeof createSignalServer> & {};

export const HostingContext = createContext<HostingContext | undefined>(
  undefined
);

export interface SessionState {
  currentSession: Session;
}

export const HostingProvider = ({
  url,
  isHosted,
  enableAnalytics,
  isScorm,
  source,
  projectId,
  versionId,
  accountId,
  children,
}: PropsWithChildren<{
  url: string;
  isHosted: boolean;
  enableAnalytics: boolean;
  source: string;
  isScorm: {};
  projectId: string;
  versionId: string;
  accountId: string;
}>) => {
  const [state, setState] = useState<SessionState | null>(null);

  const signaler = createSignalServer({
    url,
    projectId,
    versionId,
    accountId,
    source,
    isHosted,
    enableAnalytics,
    isScorm,
    getSession() {
      return state?.currentSession;
    },
    setSession(session) {
      setState(() => ({
        currentSession: session,
      }));
    },
  });

  // Start a new session
  useEffect(() => {
    signaler?.start && typeof signaler.start === 'function' && signaler.start();
  }, []);

  return (
    <HostingContext.Provider value={{ ...signaler }}>
      {children}
    </HostingContext.Provider>
  );
};

export interface Session {}

interface Event {}

const createSignalServer = ({
  url,
  projectId,
  versionId,
  accountId,
  source,
  isHosted,
  enableAnalytics,
  isScorm,
  setSession,
  getSession,
}) => {
  let localSession: any;

  // we can apply further logics here if needed...
  const isHostedOrisAnalyticsEnabled = isHosted || enableAnalytics;

  console.log(isHostedOrisAnalyticsEnabled, 'isHostedOrisAnalyticsEnabled');
  console.log(isHosted, 'isHosted');
  console.log(enableAnalytics, 'enableAnalytics');

  return {
    // Start will either
    // - create a new session
    // - create a new session from a bookmark
    async start(): Promise<Session> {
      // skip this code if we are not hosted
      if (!isHosted) {
        return {};
      }

      const csrfToken = decodeURIComponent(
        readCookie('X-CSRF-TOKEN') as string
      );

      const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({ session: { action: 'start' } }),
      });
      let json = await req.json();
      json = {
        projectId: `project_${projectId}`,
        versionId: `version_${versionId}`,
        accountId: `${accountId}`,
        userId: getLocalStorageUserId(),
        sessionId: getSessionId(),
        source,
        ...json,
      };

      localSession = json;

      setSession(json);
      return json;
    },

    // Pause the current Session. this will cause the application to create a bookmark
    // and store the current session.
    async pause(): Promise<boolean> {
      if (!isHosted) {
        return false;
      }

      try {
        const csrfToken = decodeURIComponent(
          readCookie('X-CSRF-TOKEN') as string
        );

        const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json',
          },
          body: JSON.stringify({ session: { action: 'pause' } }),
        });
        const json = await req.json();
        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    },

    // Resume the current session
    async resume(): Promise<boolean> {
      if (!isHosted) {
        return false;
      }

      try {
        const csrfToken = decodeURIComponent(
          readCookie('X-CSRF-TOKEN') as string
        );

        const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json',
          },
          body: JSON.stringify({ session: { action: 'resume' } }),
        });
        const json = await req.json();
        localSession = json;
        setSession(json);
        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    },

    // end should use sendBeacon as it might be called on window close
    async end(): Promise<void> {
      if (!isHosted) {
        return;
      }

      try {
        const csrfToken = decodeURIComponent(
          readCookie('X-CSRF-TOKEN') as string
        );

        const headers = {
          type: 'application/json',
          'X-CSRF-Token': csrfToken,
        };

        navigator.sendBeacon(
          `${url}/harp/hosting/${projectId}/session`,
          new Blob([JSON.stringify({ session: { action: 'end' } })], headers)
        );
      } catch (e) {
        console.log(e);
      }
    },

    // Gets the current session
    async current(): Promise<Session> {
      //
      if (localSession) {
        return localSession;
      }

      const stateSession = getSession();
      if (stateSession) {
        return stateSession;
      }

      console.log('no current session, generating new one');
      // should we dig into a project running in preview for its actual id ?

      let jsonReshaped: {
        source: string;
        projectId: string;
        versionId: string;
        accountId: string;
        userId: string;
        sessionId: string;
      } = {
        projectId: `project_${projectId}`,
        versionId: `version_${versionId}`,
        accountId: `${accountId}`,
        userId: getLocalStorageUserId(),
        sessionId: getSessionId(),
        source,
      };

      console.log('sessionId', jsonReshaped.sessionId);

      if (isHosted) {
        // but disabled ???
        try {
          const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
            method: 'GET',
            credentials: 'include',
          });

          const json = await req.json();

          //I think we want to turn this off for hosting as we will want to use project id
          // jsonReshaped.projectId = json.learning_module_id;

          if (enableAnalytics) {
            jsonReshaped.userId = `harp_user_${json.user_id}`;
            jsonReshaped.sessionId = `harp_session_${json.learning_module_session.id}`;
          }
        } catch (err) {
          console.warn(`${url}/harp/hosting/${projectId}/session`, err);
        }
      }

      if (isScorm?.isInitialized && enableAnalytics) {
        const scormUserId = SCORM.get(
          SCORM.version === '1.2' ? 'cmi.core.student_id' : 'cmi.learner_id '
        );

        jsonReshaped.userId =
          `scorm_user_${scormUserId}` || jsonReshaped.userId;

        // this is where it gets murky. . .
        // if bookmarking is enabled we'll get / set the sessionId in suspendData
        const isBookmarking = getStore().getState()?.theme?.enableBookmarking;

        if (isBookmarking) {
          let suspendData = SCORM.get('cmi.suspend_data');

          if (suspendData) {
            suspendData = JSON.parse(suspendData) || {};
          }

          if (suspendData?.sessionId) {
            jsonReshaped.sessionId = suspendData.sessionId;
          } else {
            SCORM.set(
              'cmi.suspend_data',
              JSON.stringify({
                ...suspendData,
                sessionId: jsonReshaped.sessionId,
              })
            );
          }

          // but then we want to account for if the learner does NOT return to bookmark
          // . . .
        }
      }

      localSession = jsonReshaped;
      // json;

      setSession(jsonReshaped);

      return jsonReshaped;
      // json;
      //
    },

    //
    async data(event: ModuleEvent): Promise<void> {
      const currentSession = (await this.current()) as any;

      const data = { ...event } as ModuleEvent & {
        projectId: string;
        accountId: string;
        versionId: string;
        userId?: string;
        sessionId: string;
        source: string;
      };

      const {
        projectId,
        accountId,
        versionId,
        userId,
        sessionId,
        source,
      } = currentSession;

      data.projectId = projectId; //  || currentSession.learning_module_id;
      data.accountId = accountId; //  || currentSession.account_id;
      data.versionId = versionId; //  || currentSession.version_id;
      data.userId = userId; //  || currentSession.user_id;
      data.sessionId = sessionId; //  || currentSession.learning_module_session.id;
      data.source = source;

      window.dispatchEvent(new CustomEvent('moduleEvent', { detail: data }));

      if (window.enableAnalytics || enableAnalytics) {
        //

        console.log((window.getParams() as GetParams).analyticsUrl);

        try {
          const req = await fetch(
            `${(window.getParams() as GetParams).analyticsUrl}/track/event`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              keepalive: true,
              body: JSON.stringify(data),
            }
          );

          const json = await req.json();

          return json;
        } catch (err) {
          console.warn(err);

          return;
        }
      }
    },
  };
};

function getLocalStorageUserId() {
  // localStorage.clear();
  //
  let userId = localStorage.getItem('userId');

  if (!userId) {
    userId = uuid() as string;
    localStorage.setItem('userId', userId);
  }

  return userId;
}

let cachedSessionId;
function getSessionId() {
  console.log('setting Session Id');
  if (cachedSessionId) {
    return cachedSessionId;
  }

  cachedSessionId = uuid();
  return cachedSessionId;
}

export const useHostingSession = () => {
  const ctx = useContext(HostingContext);
  return ctx;
};
